import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter } from "@/components/catalog/GridPaginationControls";
import { HrListToolbar, type HrQuickDatePreset } from "@/components/hr/HrListToolbar";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useGridPagination } from "@/hooks/useGridPagination";
import {
  createLeavePolicy,
  deleteLeavePolicy,
  fetchLeaveRulesCatalog,
  fetchLeaveRulesSetup,
  leavePolicyErrorMessage,
  listLeavePolicies,
  syncLeavePoliciesFromApi,
  updateLeavePolicy,
  type LeavePolicyLinkedRuleApi,
  type LeavePolicyRecord,
  type LeaveRuleCatalogItem,
} from "@/lib/hr/leavePoliciesStore";
import { listLeaveTypeRecords, syncLeaveTypesFromApi, type LeaveTypeRecord } from "@/lib/hr/leaveTypesStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, Edit, Eye, Loader2, Plus, ScrollText, Search, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

function formatPolicyDetailDate(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

function formatGlobalScalar(v: unknown): string {
  if (typeof v === "boolean") return v ? "true" : "false";
  if (v === null || v === undefined) return "";
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

type LeaveRuleRowState = {
  ruleId: string;
  code: string;
  title: string;
  description: string;
  checked: boolean;
  valueStr: string;
  boolVal: boolean;
  dataType: string;
  /** True when value comes from leave_policy_rules (customized). */
  fromPolicy: boolean;
};

type LeaveTypeRowState = {
  leaveTypeId: string;
  title: string;
  slug: string;
  checked: boolean;
};

/** Unchecked rules with Attendance Rules default values (no leave_policy_rules rows yet). */
function buildLeaveRuleRowsFromAttendanceDefaults(catalog: LeaveRuleCatalogItem[]): LeaveRuleRowState[] {
  return catalog.map((c) => {
    const dt = String(c.data_type || "string");
    const fromDefault = formatGlobalScalar(c.global_value);
    const boolVal = fromDefault === "true" || fromDefault === "1" || fromDefault === "yes";
    return {
      ruleId: String(c.id),
      code: c.code,
      title: c.title,
      description: c.description || "",
      checked: false,
      valueStr: dt === "boolean" ? (boolVal ? "true" : "false") : fromDefault,
      boolVal: dt === "boolean" ? boolVal : false,
      dataType: dt,
      fromPolicy: false,
    };
  });
}

/** Checked/customized from leave_policy_rules; missing catalog rules stay unchecked with attendance defaults. */
function buildLeaveRuleRowsFromPolicy(
  catalog: LeaveRuleCatalogItem[],
  linked: LeavePolicyLinkedRuleApi[],
): LeaveRuleRowState[] {
  const linkedByRuleId = new Map(
    linked.filter((l) => l.rule_id != null).map((l) => [String(l.rule_id), l]),
  );
  return catalog.map((c) => {
    const l = linkedByRuleId.get(String(c.id));
    const dt = String(l?.data_type || c.data_type || "string");
    if (l) {
      const fromLink = String(l.rule_value ?? "");
      const boolVal = fromLink === "true" || fromLink === "1" || fromLink === "yes";
      return {
        ruleId: String(c.id),
        code: c.code,
        title: c.title,
        description: c.description || String(l.description ?? ""),
        checked: true,
        valueStr: dt === "boolean" ? (boolVal ? "true" : "false") : fromLink,
        boolVal: dt === "boolean" ? boolVal : false,
        dataType: dt,
        fromPolicy: true,
      };
    }
    const fromDefault = formatGlobalScalar(c.global_value);
    const boolVal = fromDefault === "true" || fromDefault === "1" || fromDefault === "yes";
    return {
      ruleId: String(c.id),
      code: c.code,
      title: c.title,
      description: c.description || "",
      checked: false,
      valueStr: dt === "boolean" ? (boolVal ? "true" : "false") : fromDefault,
      boolVal: dt === "boolean" ? boolVal : false,
      dataType: dt,
      fromPolicy: false,
    };
  });
}

function rulesForLeaveType(
  catalog: LeaveRuleCatalogItem[],
  linkedAll: LeavePolicyLinkedRuleApi[],
  leaveTypeId: string,
): LeaveRuleRowState[] {
  const linked = linkedRulesForLeaveType(linkedAll, leaveTypeId);
  if (linked.length === 0) {
    return buildLeaveRuleRowsFromAttendanceDefaults(catalog);
  }
  return buildLeaveRuleRowsFromPolicy(catalog, linked);
}

function linkedRulesForLeaveType(
  linked: LeavePolicyLinkedRuleApi[],
  leaveTypeId: string,
): LeavePolicyLinkedRuleApi[] {
  return linked.filter((l) => String(l.leave_type_id ?? "") === leaveTypeId);
}

function buildLeaveTypeRows(
  allTypes: LeaveTypeRecord[],
  linked: LeavePolicyRecord["leaveTypes"] | undefined,
  selectedLeaveTypeId?: string,
): LeaveTypeRowState[] {
  const linkedById = new Map((linked ?? []).map((t) => [String(t.leaveTypeId), t]));
  const preferred =
    selectedLeaveTypeId ||
    (linked && linked.length > 0 ? String(linked[0].leaveTypeId) : "");
  return allTypes.map((t) => {
    const id = String(t.id);
    return {
      leaveTypeId: id,
      title: t.title,
      slug: t.slug,
      checked: preferred ? id === preferred : Boolean(linkedById.get(id)),
    };
  });
}

function matchesLeaveRuleSearch(r: LeaveRuleRowState, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return (
    r.title.toLowerCase().includes(n) ||
    r.code.toLowerCase().includes(n) ||
    r.description.toLowerCase().includes(n)
  );
}

function matchesLeaveTypeSearch(r: LeaveTypeRowState, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return r.title.toLowerCase().includes(n) || r.slug.toLowerCase().includes(n);
}

type SortBy = "latest" | "oldest" | "name_asc" | "name_desc" | "updated_latest" | "updated_oldest";

export default function LeavePolicies() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "leave_policy_can_view");
  const canCreate = can(user, "leave_policy_can_create");
  const canUpdate = can(user, "leave_policy_can_update");
  const canDelete = can(user, "leave_policy_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>("latest");
  const [quickDate, setQuickDate] = useState<HrQuickDatePreset>("all");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [filterName, setFilterName] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<LeavePolicyRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeavePolicyRecord | null>(null);
  const [viewPolicy, setViewPolicy] = useState<LeavePolicyRecord | null>(null);
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [ruleRows, setRuleRows] = useState<LeaveRuleRowState[]>([]);
  const [leaveTypeRows, setLeaveTypeRows] = useState<LeaveTypeRowState[]>([]);
  const [leaveTypesQuery, setLeaveTypesQuery] = useState("");
  const [rulesQuery, setRulesQuery] = useState("");
  const [rulesCatalog, setRulesCatalog] = useState<LeaveRuleCatalogItem[]>([]);
  const [linkedRulesAll, setLinkedRulesAll] = useState<LeavePolicyLinkedRuleApi[]>([]);
  const ruleDraftsRef = useRef<Record<string, LeaveRuleRowState[]>>({});

  const rows = useMemo(() => listLeavePolicies(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let m = true;
    void (async () => {
      setLoading(true);
      setIsForbidden(false);
      try {
        await syncLeavePoliciesFromApi();
      } catch (e) {
        if (m && isAxiosError(e) && e.response?.status === 403) {
          setIsForbidden(true);
          if (m) setLoading(false);
          return;
        }
        console.error(e);
        if (m) toast.error("Could not fetch leave policies. Showing cached data if any.");
      } finally {
        if (m) {
          refresh();
          setLoading(false);
        }
      }
    })();
    return () => {
      m = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((p) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(p.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(p.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today" ? startOfDay(now) : quickDate === "week" ? startOfDay(subDays(now, 6)) : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      if (filterName.trim() && !p.name.toLowerCase().includes(filterName.trim().toLowerCase())) return false;
      const matchesQuery = !q || p.name.toLowerCase().includes(q);
      return matchesQuery;
    });
  }, [
    rows,
    query,
    filterCreatedDate,
    quickDate,
    filterName,
  ]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "updated_oldest":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "updated_latest":
          return b.updatedAt.localeCompare(a.updatedAt);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterName("");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterName}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const startCreate = async () => {
    setEditing(null);
    setName("");
    setIsActive(true);
    setLeaveTypesQuery("");
    setRulesQuery("");
    setRuleRows([]);
    setLeaveTypeRows([]);
    setRulesCatalog([]);
    setLinkedRulesAll([]);
    ruleDraftsRef.current = {};
    setOpen(true);
    setRulesLoading(true);
    try {
      const [, { catalog }] = await Promise.all([syncLeaveTypesFromApi(), fetchLeaveRulesCatalog()]);
      setRulesCatalog(catalog);
      setLinkedRulesAll([]);
      setLeaveTypeRows(buildLeaveTypeRows(listLeaveTypeRecords(), undefined));
      // No leave type selected yet — show attendance defaults, all unchecked.
      setRuleRows(buildLeaveRuleRowsFromAttendanceDefaults(catalog));
    } catch (e) {
      console.error(e);
      toast.error(leavePolicyErrorMessage(e));
      setOpen(false);
    } finally {
      setRulesLoading(false);
    }
  };

  const startEdit = async (p: LeavePolicyRecord) => {
    setEditing(p);
    setName(p.name);
    setIsActive(p.isActive !== false);
    setLeaveTypesQuery("");
    setRulesQuery("");
    setRuleRows([]);
    setLeaveTypeRows([]);
    setRulesCatalog([]);
    setLinkedRulesAll([]);
    ruleDraftsRef.current = {};
    setOpen(true);
    setRulesLoading(true);
    try {
      const preferredTypeId = p.leaveTypes?.[0]?.leaveTypeId
        ? String(p.leaveTypes[0].leaveTypeId)
        : "";
      const [, setup] = await Promise.all([syncLeaveTypesFromApi(), fetchLeaveRulesSetup(p.id)]);
      setRulesCatalog(setup.catalog);
      setLinkedRulesAll(setup.linked);
      setLeaveTypeRows(buildLeaveTypeRows(listLeaveTypeRecords(), p.leaveTypes, preferredTypeId || undefined));
      if (preferredTypeId) {
        setRuleRows(rulesForLeaveType(setup.catalog, setup.linked, preferredTypeId));
      } else {
        setRuleRows(buildLeaveRuleRowsFromAttendanceDefaults(setup.catalog));
      }
    } catch (e) {
      console.error(e);
      toast.error(leavePolicyErrorMessage(e));
      setOpen(false);
    } finally {
      setRulesLoading(false);
    }
  };

  const updateRuleRow = (code: string, patch: Partial<LeaveRuleRowState>) => {
    setRuleRows((prev) => prev.map((r) => (r.code === code ? { ...r, ...patch } : r)));
  };

  const selectLeaveType = (leaveTypeId: string) => {
    const currentId = leaveTypeRows.find((r) => r.checked)?.leaveTypeId;
    if (currentId === leaveTypeId) return;

    if (currentId) {
      ruleDraftsRef.current[currentId] = ruleRows.map((r) => ({ ...r }));
    }

    setLeaveTypeRows((prev) =>
      prev.map((r) => ({
        ...r,
        checked: r.leaveTypeId === leaveTypeId,
      })),
    );

    const draft = ruleDraftsRef.current[leaveTypeId];
    if (draft) {
      setRuleRows(draft.map((r) => ({ ...r })));
      return;
    }

    // No leave_policy_rules for this type → unchecked + Attendance Rules defaults.
    // Existing rows → checked with stored values.
    setRuleRows(rulesForLeaveType(rulesCatalog, linkedRulesAll, leaveTypeId));
  };

  const filteredLeaveTypeRows = useMemo(
    () => leaveTypeRows.filter((r) => matchesLeaveTypeSearch(r, leaveTypesQuery)),
    [leaveTypeRows, leaveTypesQuery],
  );
  const filteredRuleRows = useMemo(
    () => ruleRows.filter((r) => matchesLeaveRuleSearch(r, rulesQuery)),
    [ruleRows, rulesQuery],
  );
  const selectedLeaveTypeCount = useMemo(() => leaveTypeRows.filter((r) => r.checked).length, [leaveTypeRows]);
  const selectedRuleCount = useMemo(() => ruleRows.filter((r) => r.checked).length, [ruleRows]);

  const validateLeaveRuleRows = (rowsToCheck: LeaveRuleRowState[]): string | null => {
    for (const r of rowsToCheck.filter((x) => x.checked)) {
      if (r.dataType === "boolean") continue;
      const v = r.valueStr.trim();
      if (v === "") return `Value is required for “${r.title}”.`;
      if (r.dataType === "int") {
        const n = Number(v);
        if (!Number.isFinite(n) || !Number.isInteger(n)) return `“${r.title}” must be an integer.`;
      }
      if (r.dataType === "decimal") {
        const n = Number(v);
        if (!Number.isFinite(n)) return `“${r.title}” must be a number.`;
      }
    }
    return null;
  };

  const mapRowsToRulesPayload = (rows: LeaveRuleRowState[]) =>
    rows
      .filter((r) => r.checked)
      .map((r) => {
        const rule_value =
          r.dataType === "boolean" ? (r.boolVal ? "true" : "false") : r.valueStr.trim().slice(0, 255);
        return {
          rule_id: Number(r.ruleId),
          rule_value,
          rule_type: "leave" as const,
          description: r.description || r.title,
        };
      });

  const save = async () => {
    if (!name.trim()) {
      toast.error("Policy name is required.");
      return;
    }

    // Persist the currently visible leave type into drafts before collecting all.
    const selectedType = leaveTypeRows.find((r) => r.checked);
    if (selectedType) {
      ruleDraftsRef.current[selectedType.leaveTypeId] = ruleRows.map((r) => ({ ...r }));
    }

    const leaveTypeRules: Array<{
      leave_type_id: string;
      rules: ReturnType<typeof mapRowsToRulesPayload>;
    }> = [];

    for (const [leaveTypeId, rows] of Object.entries(ruleDraftsRef.current)) {
      const validationError = validateLeaveRuleRows(rows);
      if (validationError) {
        toast.error(validationError);
        return;
      }
      const mapped = mapRowsToRulesPayload(rows);
      if (mapped.length > 0) {
        leaveTypeRules.push({ leave_type_id: leaveTypeId, rules: mapped });
      }
    }

    if (leaveTypeRules.length === 0) {
      toast.error("Select a leave type and check at least one leave rule before saving.");
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        const res = await updateLeavePolicy(editing.id, {
          name: name.trim(),
          isActive,
          leaveTypeRules,
        });
        toast.success(res?.message ?? "Updated.");
      } else {
        const res = await createLeavePolicy({
          name: name.trim(),
          isActive,
          leaveTypeRules,
        });
        toast.success(res.message);
      }
      setOpen(false);
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(leavePolicyErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await deleteLeavePolicy(deleteTarget.id);
      toast.success(res.message);
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(leavePolicyErrorMessage(e));
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Leave policies access required"
      message="You don’t have permission to view leave policies. Ask an administrator to grant leave_policy_can_view_any."
    />
  ) : (
    <Layout title="Leave policies">
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Leave policies</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Configure leave types and leave rules for each policy.{" "}
              <Link to="/dashboard" className="text-primary hover:underline font-medium">
                Home
              </Link>
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={() => void startCreate()}>
              <Plus className="w-4 h-4" /> Add policy
            </Button>
          ) : null}
        </div>

        <Dialog open={!!viewPolicy} onOpenChange={(o) => !o && setViewPolicy(null)}>
          <DialogContent className="flex max-h-[92vh] w-[96vw] max-w-5xl !flex flex-col gap-0 overflow-hidden p-0">
            <DialogHeader className="shrink-0 border-b border-border/50 bg-muted/20 px-6 py-4 pr-12">
              <DialogTitle className="text-xl font-display">{viewPolicy?.name ?? "Leave policy"}</DialogTitle>
            </DialogHeader>
            {viewPolicy ? (
              <div className="min-h-0 flex-1 overflow-auto px-6 py-5 space-y-5">
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <p className="font-medium">{viewPolicy.isActive !== false ? "Active" : "Inactive"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created</p>
                    <p className="font-medium">{formatPolicyDetailDate(viewPolicy.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Updated</p>
                    <p className="font-medium">{formatPolicyDetailDate(viewPolicy.updatedAt)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-border/60 overflow-hidden">
                    <div className="border-b border-border/50 bg-muted/40 px-4 py-2.5">
                      <h3 className="text-sm font-semibold">Leave types ({viewPolicy.leaveTypes?.length ?? 0})</h3>
                    </div>
                    <div className="max-h-[50vh] overflow-auto p-3 space-y-2">
                      {(viewPolicy.leaveTypes?.length ?? 0) === 0 ? (
                        <p className="text-xs text-muted-foreground py-4 text-center">No leave types in this policy.</p>
                      ) : (
                        viewPolicy.leaveTypes.map((t) => (
                          <div key={t.leaveTypeId} className="rounded-lg border border-border/50 bg-background px-3 py-2.5">
                            <p className="text-sm font-medium text-foreground">{t.title}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="rounded-xl border border-border/60 overflow-hidden">
                    <div className="border-b border-border/50 bg-muted/40 px-4 py-2.5">
                      <h3 className="text-sm font-semibold">Leave rules ({viewPolicy.rules?.length ?? 0})</h3>
                    </div>
                    <div className="max-h-[50vh] overflow-auto p-3 space-y-2">
                      {(viewPolicy.rules?.length ?? 0) === 0 ? (
                        <p className="text-xs text-muted-foreground py-4 text-center">No leave rules in this policy.</p>
                      ) : (
                        viewPolicy.rules.map((r) => (
                          <div key={r.ruleKey} className="rounded-lg border border-border/50 bg-background px-3 py-2.5">
                            <p className="text-sm font-medium text-foreground">{r.description || r.ruleKey}</p>
                            <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{r.ruleKey}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Value: <span className="font-medium text-foreground">{r.ruleValue}</span>
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            <DialogFooter className="shrink-0 border-t border-border/50 bg-muted/10 px-6 py-3">
              <Button variant="glass" onClick={() => setViewPolicy(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <HrListToolbar
          searchPlaceholder="Search by policy name…"
          query={query}
          onQueryChange={setQuery}
          filtersOpen={filtersOpen}
          onFiltersOpenChange={setFiltersOpen}
          appliedFiltersCount={appliedFiltersCount}
          filterSheetTitle="Filter leave policies"
          filterSheetDescription="Refine by name and created date."
          onResetSheetFilters={resetSheetFilters}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          pageSizeIdPrefix="leave-policies"
          sortBy={sortBy}
          onSortByChange={(v) => setSortBy(v as SortBy)}
          sortOptions={[
            { value: "latest", label: "Newest first" },
            { value: "oldest", label: "Oldest first" },
            { value: "name_asc", label: "Name: A to Z" },
            { value: "name_desc", label: "Name: Z to A" },
            { value: "updated_latest", label: "Recently updated" },
            { value: "updated_oldest", label: "Least recently updated" },
          ]}
          quickDate={quickDate}
          onQuickDateChange={setQuickDate}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          filterSheetChildren={
            <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
              <p className="text-sm font-medium text-foreground">Leave policy</p>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Name contains</Label>
                <Input
                  className="glass-input h-10"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  placeholder="e.g. Standard"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Created date</Label>
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn("glass-input h-10 w-full justify-start text-left font-normal", !filterCreatedDate && "text-muted-foreground")}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                      {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                    <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                    <div className="flex gap-2 border-t p-2">
                      <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                        Clear
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          }
        />

        {loading ? (
          <div className="glass-card p-8 flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {rows.length === 0
              ? canCreate
                ? "No leave policies yet. Click “Add policy” to create one and assign leave rules."
                : "No leave policies yet."
              : "No policies match your filters."}
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {paginated.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                <CatalogEntityCard
                  title={p.name}
                  primaryMeta={{
                    label: "Leave types",
                    value: String(p.leaveTypes?.length ?? 0),
                    icon: ScrollText,
                    iconClassName: "text-sky-600",
                  }}
                  secondaryMeta={{
                    label: "Updated",
                    value: formatCatalogAuditDate(p.updatedAt),
                    icon: CalendarIcon,
                    iconClassName: "text-muted-foreground",
                  }}
                  footer={`${p.rules?.length ?? 0} leave rule(s) · ${p.isActive !== false ? "Active" : "Inactive"} · Created ${formatCatalogAuditDate(p.createdAt)}`}
                  footerRight={formatCatalogAuditDate(p.updatedAt)}
                  onView={canView ? () => setViewPolicy(p) : undefined}
                  onEdit={canUpdate ? () => void startEdit(p) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(p) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {paginated.map((p) => (
              <div key={p.id} className="glass-card p-4 flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0">
                  <p className="font-semibold text-foreground">{p.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave rules: {p.rules?.length ?? 0} · {p.isActive !== false ? "Active" : "Inactive"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Added {formatCatalogAuditDate(p.createdAt)}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {canView ? (
                    <Button variant="glass" size="sm" onClick={() => setViewPolicy(p)}>
                      <Eye className="w-3.5 h-3.5" /> View
                    </Button>
                  ) : null}
                  {canUpdate ? (
                    <Button variant="glass" size="sm" onClick={() => void startEdit(p)}>
                      <Edit className="w-3.5 h-3.5" /> Edit
                    </Button>
                  ) : null}
                  {canDelete ? (
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setDeleteTarget(p)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="!flex max-h-[min(80vh,640px)] w-[92vw] max-w-3xl flex-col gap-0 overflow-hidden p-0">
            <DialogHeader className="shrink-0 border-b border-border/50 bg-muted/20 px-4 py-2.5 pr-12">
              <DialogTitle className="text-left text-lg font-display">
                {editing ? "Edit leave policy" : "New leave policy"}
              </DialogTitle>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-2.5">
              <div className="flex flex-col gap-2.5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="space-y-1 flex-1 min-w-0">
                    <Label className="text-xs text-muted-foreground">
                      Policy name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      className="glass-input h-8"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Standard staff policy"
                    />
                  </div>
                  <div className="flex h-8 items-center gap-2 sm:shrink-0 rounded-lg border border-border/50 px-2.5 bg-muted/20">
                    <Checkbox
                      checked={isActive}
                      onCheckedChange={(c) => setIsActive(c !== false)}
                      id="leave-policy-active"
                    />
                    <Label htmlFor="leave-policy-active" className="text-sm font-normal cursor-pointer whitespace-nowrap">
                      Active
                    </Label>
                  </div>
                </div>

                {rulesLoading ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-7 w-7 animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2">
                    {/* Left: Leave types */}
                    <div className="flex flex-col rounded-xl border border-border/60 bg-muted/10 shadow-sm">
                      <div className="flex items-center justify-between gap-2 border-b border-border/50 bg-muted/30 px-3 py-1.5">
                        <h3 className="text-sm font-normal text-foreground">Leave types</h3>
                        <span className="text-xs text-muted-foreground tabular-nums rounded-full bg-background px-2 py-0.5 border border-border/50">
                          {selectedLeaveTypeCount}/{leaveTypeRows.length}
                        </span>
                      </div>
                      <div className="relative px-2 pt-2">
                        <Search className="pointer-events-none absolute left-4 top-[1.05rem] h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          className="glass-input h-8 pl-8 text-xs"
                          placeholder="Search leave types…"
                          value={leaveTypesQuery}
                          onChange={(e) => setLeaveTypesQuery(e.target.value)}
                          autoComplete="off"
                        />
                      </div>
                      <div className="space-y-1 p-2">
                        {leaveTypeRows.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-4 text-center">
                            No leave types found.{" "}
                            <Link to="/leave-types" className="text-primary font-medium hover:underline">
                              Create leave types
                            </Link>{" "}
                            first.
                          </p>
                        ) : filteredLeaveTypeRows.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-4 text-center">No matches.</p>
                        ) : (
                          filteredLeaveTypeRows.map((r) => (
                            <button
                              key={r.leaveTypeId}
                              type="button"
                              onClick={() => selectLeaveType(r.leaveTypeId)}
                              className={cn(
                                "w-full text-left rounded-lg border px-2.5 py-1.5 transition-colors cursor-pointer",
                                r.checked
                                  ? "border-primary/40 bg-primary/10 ring-1 ring-primary/20"
                                  : "border-border/50 bg-background/80 hover:bg-muted/40",
                              )}
                            >
                              <p className="text-sm font-normal text-foreground leading-snug truncate">{r.title}</p>
                            </button>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Right: Leave rules */}
                    <div className="flex flex-col rounded-xl border border-border/60 bg-muted/10 shadow-sm">
                      <div className="flex items-center justify-between gap-2 border-b border-border/50 bg-muted/30 px-3 py-1.5">
                        <h3 className="text-sm font-normal text-foreground">Leave rules</h3>
                        <span className="text-xs text-muted-foreground tabular-nums rounded-full bg-background px-2 py-0.5 border border-border/50">
                          {selectedRuleCount}/{ruleRows.length}
                        </span>
                      </div>
                      <div className="relative px-2 pt-2">
                        <Search className="pointer-events-none absolute left-4 top-[1.05rem] h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          className="glass-input h-8 pl-8 text-xs"
                          placeholder="Search leave rules…"
                          value={rulesQuery}
                          onChange={(e) => setRulesQuery(e.target.value)}
                          autoComplete="off"
                        />
                      </div>
                      <div className="space-y-1 p-2">
                        {ruleRows.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-4 text-center">
                            No leave rules in catalog.
                          </p>
                        ) : filteredRuleRows.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-4 text-center">No matches.</p>
                        ) : (
                          <TooltipProvider delayDuration={250}>
                            {filteredRuleRows.map((r) => (
                              <div
                                key={r.code}
                                className={cn(
                                  "flex items-center gap-2 rounded-lg border px-2 py-1 transition-colors",
                                  r.checked
                                    ? "border-primary/40 bg-primary/10"
                                    : "border-border/50 bg-background/80",
                                )}
                              >
                                <Checkbox
                                  checked={r.checked}
                                  onCheckedChange={(c) => updateRuleRow(r.code, { checked: c === true })}
                                  id={`leave-rule-${r.code}`}
                                  className="shrink-0"
                                />
                                {r.description ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Label
                                        htmlFor={`leave-rule-${r.code}`}
                                        className="min-w-0 flex-1 cursor-pointer truncate text-sm font-normal text-foreground leading-snug"
                                      >
                                        {r.title}
                                      </Label>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="max-w-xs text-xs leading-snug">
                                      {r.description}
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <Label
                                    htmlFor={`leave-rule-${r.code}`}
                                    className="min-w-0 flex-1 cursor-pointer truncate text-sm font-normal text-foreground leading-snug"
                                  >
                                    {r.title}
                                  </Label>
                                )}
                                <div className="shrink-0">
                                  {r.dataType === "boolean" ? (
                                    <Switch
                                      checked={r.boolVal}
                                      onCheckedChange={(v) =>
                                        updateRuleRow(r.code, {
                                          boolVal: v,
                                          valueStr: v ? "true" : "false",
                                          checked: true,
                                        })
                                      }
                                    />
                                  ) : (
                                    <Input
                                      className="glass-input h-7 w-[5rem] text-xs text-right tabular-nums"
                                      value={r.valueStr}
                                      onChange={(e) =>
                                        updateRuleRow(r.code, {
                                          valueStr: e.target.value,
                                          checked: true,
                                        })
                                      }
                                      placeholder={
                                        r.dataType === "int" ? "0" : r.dataType === "decimal" ? "0.0" : "…"
                                      }
                                    />
                                  )}
                                </div>
                              </div>
                            ))}
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="shrink-0 border-t border-border/60 bg-muted/10 px-4 py-2.5">
              <Button variant="glass" onClick={() => setOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void save()} disabled={saving || rulesLoading}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this policy?"
          description="This will permanently delete the leave policy and its leave type rules."
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
}
